import styles from "./layout.css";

/**
 * Renders a layout with optional header, sidebar, footer, and customizable configuration options.
 *
 * @param {Object} props - The properties passed to the Layout component.
 * @param {React.Component|null} [props.Header=null] - The optional component to render as the header.
 * @param {React.Component|null} [props.Sidebar=null] - The optional component to render as the sidebar.
 * @param {React.Component|null} [props.Footer=null] - The optional component to render as the footer.
 * @param {React.ReactNode} props.children - The content to be displayed in the main area of the layout.
 * @param {boolean} [props.hasLibrary=false] - Determines if there is a library section in the middle layout.
 * @param {boolean} [props.centerVertically=false] - Centers the content vertically in the main area if true.
 * @param {boolean} [props.userTour=false] - Adds specific styling for user tour functionality if true.
 * @param {boolean} [props.isDarkMode=false] - Toggles dark mode styling when set to true.
 * @param {boolean} [props.noPaddingTop=false] - Removes top padding if true.
 * @param {boolean} [props.noPaddingBottom=false] - Removes bottom padding if true.
 *
 * @return {JSX.Element} The rendered layout containing optional header, sidebar, footer, and main content areas.
 */
function Layout({
  Header = null,
  Sidebar = null,
  Footer = null,
  children,
  hasLibrary = false,
  centerVertically = false,
  userTour = false,
  isDarkMode = false,
  noPaddingTop = false,
  noPaddingBottom = false,
}) {
  return (
    <div className={styles.layout}>
      {Header && <Header />}
      <div
        className={styles.middle({
          sidebar: Sidebar !== null,
          library: hasLibrary || null,
          centerVertically: centerVertically,
          userTour: userTour,
          darkMode: isDarkMode,
          noPaddingTop: noPaddingTop,
          noPaddingBottom: noPaddingBottom,
        })}
      >
        {Sidebar && (
          <nav className={styles.sidebar}>
            <Sidebar />
          </nav>
        )}
        <main
          className={styles.content({
            sidebar: Sidebar !== null,
            centerVertically: centerVertically,
          })}
        >
          {children}
        </main>
      </div>
      {Footer && <Footer />}
    </div>
  );
}

export default Layout;
